import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

export interface PSUser {
  id: number;
  email: string;
  name: string;
  picture: string;
  is_plus: number; // 0 or 1 from SQLite
  /** Epoch seconds the subscription lapses; null unless it is set to cancel. */
  cancel_at?: number | null;
}

interface AuthContextType {
  user: PSUser | null;
  loading: boolean;
  token: string | null;
  /** Opens the sign-in dialog (email/password, plus the Google button). */
  login: () => void;
  loginWithGoogle: () => void;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  registerWithPassword: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => void;
  authDialogOpen: boolean;
  closeAuthDialog: () => void;
  /** True when loginWithGoogle() was called from an in-app browser — show InAppBrowserModal */
  inAppBrowserBlocked: boolean;
  dismissInAppBrowserModal: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  token: null,
  login: () => {},
  loginWithGoogle: () => {},
  loginWithPassword: async () => {},
  registerWithPassword: async () => {},
  logout: () => {},
  refreshUser: () => {},
  authDialogOpen: false,
  closeAuthDialog: () => {},
  inAppBrowserBlocked: false,
  dismissInAppBrowserModal: () => {},
});

// Detect in-app / embedded WebView browsers that Google OAuth blocks
export function isInAppBrowser(): boolean {
  const ua = navigator.userAgent || "";
  return (
    /FBAN|FBAV/.test(ua) ||          // Facebook
    /Instagram/.test(ua) ||           // Instagram
    /Twitter/.test(ua) ||             // Twitter / X
    /\bwv\b/.test(ua) ||             // Android WebView flag
    // iOS WebView: AppleWebKit present but Safari absent
    (/iPhone|iPad/.test(ua) && /AppleWebKit/.test(ua) && !/Safari/.test(ua))
  );
}

// Detect environment
const { hostname } = window.location;
const isLocalNetwork =
  hostname === "localhost" ||
  hostname === "127.0.0.1" ||
  /^192\.168\.\d+\.\d+$/.test(hostname) ||
  /^10\.\d+\.\d+\.\d+$/.test(hostname) ||
  /^172\.(1[6-9]|2\d|3[01])\.\d+\.\d+$/.test(hostname);

export const API_BASE = isLocalNetwork
  ? `http://${hostname === "localhost" ? "127.0.0.1" : hostname}:8008`
  : "https://oriolebird.pythonanywhere.com";

export function AuthProvider({ children, prod }: { children: React.ReactNode; prod?: boolean }) {
  const authBase = prod ? "https://oriolebird.pythonanywhere.com" : API_BASE;
  const [user, setUser]     = useState<PSUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken]   = useState<string | null>(() =>
    localStorage.getItem("ps_token")
  );
  const [inAppBrowserBlocked, setInAppBrowserBlocked] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);

  const fetchMe = useCallback(async (t: string) => {
    try {
      const res = await fetch(`${authBase}/auth/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (res.ok) {
        setUser(await res.json());
      } else {
        localStorage.removeItem("ps_token");
        setToken(null);
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [authBase]);

  useEffect(() => {
    if (token) {
      fetchMe(token);
    } else {
      setLoading(false);
    }
  }, [token, fetchMe]);

  const login = () => setAuthDialogOpen(true);

  const loginWithGoogle = () => {
    if (isInAppBrowser()) {
      setAuthDialogOpen(false);
      setInAppBrowserBlocked(true);
      return;
    }
    const returnTo = encodeURIComponent(window.location.origin);
    window.location.href = `${authBase}/auth/google?return_to=${returnTo}`;
  };

  const startSession = (t: string, u: PSUser) => {
    localStorage.setItem("ps_token", t);
    setToken(t);
    setUser(u);
    setLoading(false);
    setAuthDialogOpen(false);
  };

  const postAuth = async (path: string, body: object) => {
    let res: Response;
    try {
      res = await fetch(`${authBase}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    } catch {
      throw new Error("Could not reach the server. Please try again.");
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "Something went wrong. Please try again.");
    startSession(data.token, data.user);
  };

  const loginWithPassword = (email: string, password: string) =>
    postAuth("/auth/login", { email, password });

  const registerWithPassword = (name: string, email: string, password: string) =>
    postAuth("/auth/register", { name, email, password });

  const closeAuthDialog = () => setAuthDialogOpen(false);

  const dismissInAppBrowserModal = () => setInAppBrowserBlocked(false);

  const logout = () => {
    localStorage.removeItem("ps_token");
    setToken(null);
    setUser(null);
  };

  const refreshUser = () => {
    const t = localStorage.getItem("ps_token");
    if (t) {
      setToken(t);
      fetchMe(t);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        token,
        login,
        loginWithGoogle,
        loginWithPassword,
        registerWithPassword,
        logout,
        refreshUser,
        authDialogOpen,
        closeAuthDialog,
        inAppBrowserBlocked,
        dismissInAppBrowserModal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
